// iGaming Radar client (api/cloudflare/src/routes/admin/radar.ts): the imported
// snapshot datasets, filtered in the browser, and the SerpApi checks.
import { api } from './client';

const BASE = '/admin/radar';

export interface RadarMeta {
  exported_at: string; imported_at: string;
  brands: number; facts: number; affiliates: number;
  demand_brands: number; countries: number; demand_first: string; demand_last: string;
}

export interface RadarFact {
  field: string;
  value: unknown;
  source_id: string; source_url: string; observed_at: string;
}

export interface RadarBrand {
  id: string; name: string; category: string; query: string;
  source_url: string | null; source_label: string;
  status: 'candidate' | 'inferred' | 'query_corrected' | 'query_review' | string;
  sources: { source_id: string; source_url: string; raw_name: string; domain: string | null }[];
  facts: RadarFact[];
}

export interface RadarImport {
  source_id: string; label: string; url: string; fetched_at: string;
  records: number; coverage: string; brands: number;
}

export interface RadarBrands { imports: RadarImport[]; brands: RadarBrand[] }

export interface AffiliateClaim {
  name?: string; website?: string;
  geo: { target: string[]; forbidden: string[] };
  models?: string[];
  fields?: Record<string, string>;
  overview?: string;
  verticals?: string[];
  traffic?: string[];
  source_updated?: string | null;
  conflicts?: string[];
  source_url: string;
  verification: 'official_site' | 'directory_claim' | string;
  observed_at: string;
}

export interface AffiliateProgram {
  id: string; name: string; kind: 'program' | 'network' | 'advertiser' | string; website: string | null;
  claims: AffiliateClaim[];
}

export interface DemandCountry { code: string; name: string; playLang: string }

export interface DemandPlay {
  state: string; date: string; count: number; error: string | null;
  apps: { id: string; title: string; developer: string | null; position: number }[];
}

export interface DemandBrand {
  id: number; name: string; query: string; observedAt: string;
  /** Trends Score per country, in DemandData.countries order. */
  values: number[];
  /** [apple 0–100, wordstat 0–100, bing 0–100, wordstat raw, bing raw] by country code. */
  interest: Record<string, (number | null)[]>;
  play: Record<string, DemandPlay>;
}

export interface DemandData {
  first: string; last: string;
  countries: DemandCountry[];
  brands: DemandBrand[];
  excludedQueries: string[];
  periods: { apple: string; wordstat: string; bing: string };
}

export type CheckKind = 'trends' | 'play';

export interface TrendsResult {
  timeline: { date: string; timestamp: string | null; value: number | null; partial: boolean }[];
  empty: boolean;
}
export interface PlayResult {
  apps: { id: string; title: string; link: string; author: string | null; rating: number | null; downloads: string | null }[];
  count: number;
}

export interface RadarCheck {
  id: number; brand_id: string; kind: CheckKind; query: string; country: string; period: string;
  checked_at: string; admin_name: string | null; cached: boolean;
  result: TrendsResult | PlayResult;
}

export interface RadarChecks { checks: RadarCheck[]; used: number; limit: number; key_configured: boolean }

export interface CheckInput { brand_id: string; kind: CheckKind; query: string; country: string; period: string }

export const getRadarMeta = () => api.get<RadarMeta | null>(`${BASE}/data/meta`);
export const getRadarBrands = () => api.get<RadarBrands>(`${BASE}/data/brands`);
export const getRadarAffiliates = () => api.get<{ programs: AffiliateProgram[] }>(`${BASE}/data/affiliates`);
export const getRadarDemand = () => api.get<DemandData>(`${BASE}/data/demand`);
export const getRadarChecks = () => api.get<RadarChecks>(`${BASE}/checks`);
export const runRadarCheck = (input: CheckInput) => api.post<RadarCheck>(`${BASE}/check`, input);
